import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchUpcomingEvents } from "@/lib/events.functions";
import { EventCard } from "@/components/upcoming-events-section";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/community/events/")({
  head: () => ({
    meta: [
      { title: "UK Card Shows & Trading Card Events | BidNova Community" },
      {
        name: "description",
        content:
          "The BidNova UK card show calendar — upcoming trading card shows, tournaments, trade nights and grading events.",
      },
      { property: "og:title", content: "UK Card Show Calendar — BidNova Community" },
      {
        property: "og:description",
        content: "Upcoming UK card shows, tournaments and trade nights.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/community/events" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/community/events" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Community", path: "/community" }, { name: "Events", path: "/community/events" }])],
  }),
  component: CommunityEventsPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function CommunityEventsPage() {
  const { t } = useTranslation("community");
  const { data, isLoading } = useQuery({
    queryKey: ["upcoming-events", "community", "all"],
    queryFn: () => fetchUpcomingEvents(48),
    staleTime: 60_000,
  });
  const items = data ?? [];
  return (
    <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-10 pb-16">
      <div className="mb-8">
        <h2 className="font-display text-3xl text-white">{t("events.communityIndex.title", "UK Card Show Calendar")}</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {t("events.communityIndex.subtitle", "Trading card shows, tournaments, trade nights, grading submission events and collector meetups across the UK.")}
        </p>
      </div>
      {isLoading ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[320px] rounded-2xl border border-white/10 skeleton-shimmer" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-muted-foreground">
          {t("events.communityIndex.empty", "No upcoming events yet — check back soon.")}
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((ev) => <EventCard key={ev.id} ev={ev} />)}
        </div>
      )}
    </div>
  );
}
